import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import styles from './Dashboard.module.css';

// ── chart / content components ────────────────────────────────────────────────
import LineChart from './LineChart';
import StatusBarChart from './StatusBarChart';
import PriorityPieChart from './PriorityPieChart';
import DepartmentBarChart from './DepartmentBarChart';
import ResolvedLineChart from './ResolvedLineChart';
import AvgResponseTimeChart from './AvgResponseTimeChart';
import AgentBarChart from './AgentBarChart';
import TopAgentsChart from './TopAgentsChart';
import ClaritaskComparisonChart from './ClaritaskComparisonChart';
import RecentActivity from './RecentActivity';
import ClosedTicketsTicker from './ClosedTicketsTicker';
import LastUpdatedTickets from './LastUpdatedTickets';

// ── stat card config ──────────────────────────────────────────────────────────
const STAT_CONFIG = {
    stat_total:               { label: 'Total Tickets',       link: '/tickets',                        color: '#2563eb' },
    stat_open:                { label: 'Open',                link: '/tickets/open',                   color: '#dc2626' },
    stat_closed:              { label: 'Closed',              link: '/tickets/closed',                 color: '#16a34a' },
    stat_pending:             { label: 'Pending',             link: '/tickets/pending',                color: '#d97706' },
    stat_overdue:             { label: 'Overdue',             link: '/tickets?due_date=overdue',       color: '#b91c1c' },
    stat_need_confirmation:   { label: 'Need Confirmation',   link: '/tickets/need confirmation',      color: '#7c3aed' },
    stat_need_clarification:  { label: 'Need Clarification',  link: '/tickets/need clarification',   color: '#0891b2' },
    stat_in_progress:         { label: 'In Progress',         link: '/tickets/in progress',            color: '#0284c7' },
    stat_active:              { label: 'Active',              link: '/tickets/active',                 color: '#059669' },
};

// ── stat widget renderer ──────────────────────────────────────────────────────
const StatWidget = ({ code, ticketsCount, editable }) => {
    const cfg = STAT_CONFIG[code];
    if (!cfg || !ticketsCount) return null;

    const dragRef = React.useRef(false);

    const helperKeys = new Set(['Unassigned', 'Assigned', 'MyTickets']);
    const total = Object.entries(ticketsCount)
        .filter(([k]) => !helperKeys.has(k) && k !== 'overdue')
        .reduce((sum, [, v]) => sum + (v || 0), 0);

    const getValue = () => {
        if (code === 'stat_total') return total;
        const key = code.replace('stat_', '').replace(/_/g, ' ');
        const found = Object.entries(ticketsCount).find(
            ([k]) => k.toLowerCase() === key.toLowerCase()
        );
        return found ? (found[1] ?? 0) : 0;
    };

    const value = getValue();

    const inner = (
        <div style={{ padding: '4px 12px 8px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{value}</span>
            </div>
        </div>
    );

    if (!cfg.link || editable) return inner;

    return (
        <Link
            to={cfg.link}
            style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
            onMouseDown={() => { dragRef.current = false; }}
            onMouseMove={() => { dragRef.current = true; }}
            onClick={(e) => { if (dragRef.current) { dragRef.current = false; e.preventDefault(); } }}
        >
            {inner}
        </Link>
    );
};

// ── shared icon helpers ───────────────────────────────────────────────────────
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

const ConfirmRemovePortal = ({ anchorRef, onConfirm, onCancel }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const portalRef = useRef(null);

    useEffect(() => {
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setPos({
                top: rect.bottom + window.scrollY + 6,
                left: rect.right + window.scrollX,
            });
        }

        const handleClickOutside = (e) => {
            const inAnchor = anchorRef.current && anchorRef.current.contains(e.target);
            const inPortal = portalRef.current && portalRef.current.contains(e.target);
            if (!inAnchor && !inPortal) {
                onCancel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [anchorRef, onCancel]);

    return createPortal(
        <div
            ref={portalRef}
            style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                transform: 'translateX(-100%)',
                zIndex: 99999,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                padding: '10px 14px',
                minWidth: 200,
                fontSize: '.85rem',
            }}
        >
            <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#111827' }}>Remove widget?</p>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className={`${styles.btnManage} ${styles.btnDanger}`} onClick={onConfirm}>Remove</button>
                <button className={styles.btnManage} onClick={onCancel}>Cancel</button>
            </div>
        </div>,
        document.body
    );
};

// ── widget body renderer ──────────────────────────────────────────────────────
const renderWidgetBody = (widget, dashboardData, useSampleData, editable) => {
    if (!dashboardData) return null;

    // individual stat cards
    if (widget.code.startsWith('stat_')) {
        return <StatWidget code={widget.code} ticketsCount={dashboardData.ticketsCount} editable={editable} />;
    }

    switch (widget.code) {
        case 'tickets_created':
            return <LineChart dailyTicketsChart={dashboardData.dailyTicketsChart} />;
        case 'tickets_by_status':
            return <StatusBarChart ticketsCount={dashboardData.ticketsCount} />;
        case 'tickets_by_priority':
            return <PriorityPieChart priorityData={dashboardData.priorityData} />;
        case 'tickets_by_department':
            return <DepartmentBarChart departments={dashboardData.departments} />;
        case 'resolved_tickets':
            return <ResolvedLineChart resolvedTicketsChart={dashboardData.resolvedTicketsChart} />;
        case 'avg_response_time':
            return <AvgResponseTimeChart avgResponseTimeChart={dashboardData.avgResponseTimeChart} />;
        case 'tickets_per_agent':
            return <AgentBarChart ticketsPerAgent={dashboardData.ticketsPerAgent} />;
        case 'top_agents':
            return <TopAgentsChart agents={dashboardData.topAgents || []} />;
        case 'claritask_comparison':
            return <ClaritaskComparisonChart claritaskTicketStats={dashboardData.claritaskTicketStats} />;
        case 'recent_activity':
            return <RecentActivity activities={dashboardData.recentActivity || []} useSampleData={useSampleData} />;
        case 'closed_tickets':
            return <ClosedTicketsTicker tickets={dashboardData.closedTicketsTicker || []} useSampleData={useSampleData} />;
        case 'last_updated_tickets':
            return <LastUpdatedTickets tickets={dashboardData.lastUpdatedTickets || []} useSampleData={useSampleData} />;
        default:
            return <div style={{ padding: 16, color: '#9ca3af', fontSize: '.85rem' }}>Unknown widget: {widget.code}</div>;
    }
};

// ── main Widget component ─────────────────────────────────────────────────────
const Widget = ({ widget, onRemove, editable, dashboardData, useSampleData }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const btnRef = useRef(null);

    const stopPropagation = (e) => e.stopPropagation();

    return (
        <div className={`${styles.widgetCard} ${editable ? styles.editable : ''} ${editable ? 'widget-drag-handle' : ''}`} style={{ height: '100%' }}>

            {/* header */}
            <div className={styles.widgetHead}>
                <span className={styles.widgetTitle}>{widget.name}</span>
                {editable && (
                    <div
                        className={`${styles.widgetActions} widget-no-drag`}
                        onMouseDown={stopPropagation}
                        onTouchStart={stopPropagation}
                    >
                        <button
                            ref={btnRef}
                            className={`${styles.widgetActionBtn} ${styles.danger}`}
                            title="Remove widget"
                            onClick={() => setConfirmOpen(v => !v)}
                        >
                            <TrashIcon />
                        </button>
                        {confirmOpen && (
                            <ConfirmRemovePortal
                                anchorRef={btnRef}
                                onConfirm={() => { setConfirmOpen(false); onRemove(widget.layout_id); }}
                                onCancel={() => setConfirmOpen(false)}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className={styles.widgetBody}>
                {widget.subtitle && !widget.code.startsWith('stat_') && (
                    <p style={{ margin: '0 12px 6px', fontSize: '.8rem', color: '#6b7280', flexShrink: 0 }}>{widget.subtitle}</p>
                )}
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    {renderWidgetBody(widget, dashboardData, useSampleData, editable)}
                </div>
            </div>
        </div>
    );
};

export default Widget;
